import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Sword, Compass, Sparkles, AlertTriangle, CalendarClock, BookOpen, Info
} from "lucide-react";

/**
 * Detailed Kundli analysis panel with tabs:
 * Aspects · Yogas · Doshas · Digbala · 5-Year Forecast · Laal Kitab
 */
export default function KundliAnalysisPanel({ kundli }) {
  const [tab, setTab] = useState("aspects");
  if (!kundli) return null;

  return (
    <Card className="p-4 sm:p-6 rounded-2xl border-slate-200 dark:border-slate-800" data-testid="kundli-analysis-panel">
      <div className="flex items-center gap-2 mb-4">
        <Info className="w-4 h-4 text-amber-500" />
        <h3 className="font-display text-lg sm:text-xl">Detailed Vedic Analysis</h3>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-full flex-wrap h-auto p-1">
          <TabsTrigger value="aspects" className="rounded-full text-xs" data-testid="tab-aspects"><Sword className="w-3 h-3 mr-1" />Aspects</TabsTrigger>
          <TabsTrigger value="yogas" className="rounded-full text-xs" data-testid="tab-yogas"><Sparkles className="w-3 h-3 mr-1" />Yogas</TabsTrigger>
          <TabsTrigger value="doshas" className="rounded-full text-xs" data-testid="tab-doshas"><AlertTriangle className="w-3 h-3 mr-1" />Doshas</TabsTrigger>
          <TabsTrigger value="digbala" className="rounded-full text-xs" data-testid="tab-digbala"><Compass className="w-3 h-3 mr-1" />Digbala</TabsTrigger>
          <TabsTrigger value="forecast" className="rounded-full text-xs" data-testid="tab-forecast"><CalendarClock className="w-3 h-3 mr-1" />5-Year</TabsTrigger>
          <TabsTrigger value="laalkitab" className="rounded-full text-xs" data-testid="tab-laalkitab"><BookOpen className="w-3 h-3 mr-1" />Laal Kitab</TabsTrigger>
        </TabsList>

        <TabsContent value="aspects" className="mt-4 space-y-2" data-testid="aspects-content">
          <p className="text-xs text-slate-500 mb-2">Vedic Drishti — houses influenced by each planet from its placement.</p>
          {(kundli.aspects || []).map(a => (
            <div key={a.planet} className="p-3 rounded-xl border border-slate-200 dark:border-slate-800">
              <div className="flex items-center justify-between">
                <div className="font-medium">{a.planet}</div>
                <div className="text-xs text-slate-500">From House {a.from_house} · {a.from_rashi}</div>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-widest text-slate-500">Aspects:</span>
                {a.aspects_houses.map(h => (
                  <Badge key={h} variant="secondary" className="text-[10px] font-mono">House {h}</Badge>
                ))}
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-300 mt-1.5 italic">{a.notes}</p>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="yogas" className="mt-4 space-y-2" data-testid="yogas-content">
          <p className="text-xs text-slate-500 mb-2">Auspicious and cautionary planetary combinations detected in this chart.</p>
          {(kundli.yogas || []).length === 0 && (
            <p className="text-sm text-slate-500">No prominent yogas detected in the standard set.</p>
          )}
          {(kundli.yogas || []).map(y => (
            <div key={y.name + y.planet} className={`p-3 rounded-xl border ${
              y.strength === "Caution" ? "border-red-200 dark:border-red-500/40 bg-red-50/30 dark:bg-red-500/5" :
              y.strength === "Strong" ? "border-emerald-200 dark:border-emerald-500/40 bg-emerald-50/30 dark:bg-emerald-500/5" :
              "border-amber-200 dark:border-amber-500/40 bg-amber-50/30 dark:bg-amber-500/5"
            }`} data-testid={`yoga-${y.name.replace(/\s+/g,'-').toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <div className="font-medium">{y.name}</div>
                <Badge variant="secondary" className="text-[10px]">{y.strength}</Badge>
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">{y.type} · {y.planet}</div>
              <p className="text-sm text-slate-700 dark:text-slate-200 mt-1.5">{y.description}</p>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="doshas" className="mt-4 space-y-2" data-testid="doshas-content">
          <p className="text-xs text-slate-500 mb-2">Karmic afflictions and remedies (only reported dosha are meaningful — absent doshas confirm no affliction).</p>
          {(kundli.doshas || []).map(d => (
            <div key={d.name} className={`p-3 rounded-xl border ${
              d.present ? "border-red-200 dark:border-red-500/40 bg-red-50/30 dark:bg-red-500/5" :
              "border-emerald-200 dark:border-emerald-500/40 bg-emerald-50/30 dark:bg-emerald-500/5"
            }`} data-testid={`dosha-${d.name.replace(/\s+/g,'-').toLowerCase()}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="font-medium flex items-center gap-2">
                  {d.name}
                  {d.present ? <Badge className="bg-red-100 text-red-800 text-[10px]">Present</Badge>
                              : <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Absent</Badge>}
                </div>
                {d.severity && <Badge variant="secondary" className="text-[10px]">{d.severity}</Badge>}
              </div>
              <p className="text-sm text-slate-700 dark:text-slate-200 mt-1.5">{d.reason}</p>
              {d.remedy && (
                <div className="mt-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30">
                  <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-400 mb-0.5">Remedy</div>
                  <p className="text-xs text-slate-700 dark:text-slate-200">{d.remedy}</p>
                </div>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="digbala" className="mt-4" data-testid="digbala-content">
          <p className="text-xs text-slate-500 mb-3">Directional strength — how well each planet is placed relative to its best house.</p>
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Planet</th>
                  <th className="text-center px-2 py-2">Best</th>
                  <th className="text-center px-2 py-2">Current</th>
                  <th className="text-left px-3 py-2">Strength</th>
                  <th className="text-left px-3 py-2">Verdict</th>
                </tr>
              </thead>
              <tbody>
                {(kundli.digbala || []).map(d => (
                  <tr key={d.planet} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="px-3 py-1.5 font-medium">{d.planet}</td>
                    <td className="px-2 py-1.5 text-center font-mono text-slate-500">H{d.best_house}</td>
                    <td className="px-2 py-1.5 text-center font-mono text-amber-600">H{d.current_house}</td>
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                          <div className={`h-full rounded-full ${
                            d.strength_pct >= 65 ? "bg-emerald-500" :
                            d.strength_pct >= 40 ? "bg-amber-500" : "bg-red-500"
                          }`} style={{ width: `${d.strength_pct}%` }} />
                        </div>
                        <span className="text-xs font-mono">{d.strength_pct}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-1.5">
                      <Badge variant="secondary" className="text-[10px]">{d.verdict}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="forecast" className="mt-4 space-y-2" data-testid="forecast-content">
          <p className="text-xs text-slate-500 mb-2">Next 5 years of Antardashas (sub-periods within the running Mahadasha).</p>
          {(kundli.five_year_forecast || []).length === 0 && (
            <p className="text-sm text-slate-500">No upcoming dashas found in the horizon.</p>
          )}
          {(kundli.five_year_forecast || []).map((f, i) => (
            <div key={`${f.mahadasha}-${f.antardasha}-${f.start}`} className={`p-3 rounded-xl border ${
              i === 0 ? "border-amber-300 bg-amber-50/40 dark:bg-amber-500/5" :
              "border-slate-200 dark:border-slate-800"
            }`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-mono text-xs text-slate-500">{f.start.slice(0,10)} → {f.end.slice(0,10)}</div>
                  <div className="font-medium mt-0.5">{f.antardasha} in {f.mahadasha} <span className="text-xs text-slate-500">· {f.years} yr</span></div>
                </div>
                {i === 0 && <Badge className="bg-amber-100 text-amber-800 text-[10px]">Active</Badge>}
              </div>
              <p className="text-sm text-slate-700 dark:text-slate-200 mt-1.5 italic">{f.theme}</p>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="laalkitab" className="mt-4 space-y-2" data-testid="laalkitab-content">
          <p className="text-xs text-slate-500 mb-2">Laal Kitab guidance — house-based interpretation with practical remedies.</p>
          {(kundli.laal_kitab || []).map(g => (
            <div key={g.planet} className="p-3 rounded-xl border border-slate-200 dark:border-slate-800">
              <div className="flex items-center justify-between">
                <div className="font-medium">{g.planet} <span className="text-xs text-slate-500 font-mono ml-1">H{g.house} · {g.rashi}</span></div>
              </div>
              <p className="text-sm text-slate-700 dark:text-slate-200 mt-1.5">{g.guidance}</p>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </Card>
  );
}
